import { Controller } from "@hotwired/stimulus"
import videojs from "video.js"

globalThis.feedMuted ??= true // start muted for mobile autoplay

// Tracks the single VideoJsController currently allowed to play, across
// the whole feed (not just within one post). Whenever any video starts
// playing - manually or via visibility-driven autoplay - the previous
// holder gets paused. This is what guarantees only one video plays at a
// time feed-wide.
let currentlyPlayingController = null

// Keeps a pristine copy of each <video> element, keyed by the stable id
// rendered on its wrapper (e.g. "video-js-42"). Video.js mutates the
// element it's given (adds the vjs-tech class, wraps it in control-bar
// markup) and eventually disposes it. If a page is ever restored from
// Turbo's cache in a state where that mutation/disposal already happened
// - or the live element is simply missing - this lets connect() recover
// by swapping in a fresh, untouched clone instead of erroring.
const pristineVideoCache = new Map()

export default class VideoJsController extends Controller {
  static targets = ["video"]

  connect() {
    if (!this.ensureVideoElement()) return

    // Every play()/pause() call is chained onto this promise so they run
    // strictly one after another for this player. Without this, a pause()
    // fired while a play() promise is still pending throws "The play()
    // request was interrupted by a call to pause()" and the video is left
    // paused.
    this.operationQueue = Promise.resolve()

    this.player = videojs(this.videoTarget, {
      controls: true,
      controlBar: {
        children: ["playToggle", "progressControl", "volumePanel", "fullscreenToggle"]
      },
      autoplay: false,
      preload: "none",
      loop: true,
      playsinline: true,
      muted: globalThis.feedMuted
    })

    this.addCenterPlayButton()

    this.player.ready(() => {
      this.element.videoController = this
      this.player.muted(globalThis.feedMuted)

      // Tell the feed controller this player can now be played, in case
      // it became visible before Video.js finished initializing.
      this.element
        .closest("[data-controller~='feed-video']")
        ?.feedVideoController?.handleVideoReady()
    })

    // Global mute sync
    this.player.on("volumechange", this.volumeChanged)

    // Whenever this video plays - whether from a manual click or our own
    // visibility-driven play() - pause whatever else was playing anywhere
    // in the feed, so only one video ever plays at a time.
    this.player.on("play", () => {
      if (currentlyPlayingController && currentlyPlayingController !== this) {
        currentlyPlayingController.pause()
      }
      currentlyPlayingController = this
    })

    this.player.on("pause", () => {
      if (currentlyPlayingController === this) {
        currentlyPlayingController = null
      }
    })

    document.addEventListener("feed:mute-change", this.muteListener)

    // Turbo freezes a snapshot of the current page (for instant back/
    // forward restores) via turbo:before-cache. Video.js patches native
    // methods directly onto the <video> element; if that patched state
    // gets frozen into the cache, restoring it later and initializing a
    // fresh player on top can throw "Illegal invocation" from inside
    // Video.js's tech setup. Disposing here, before the snapshot is
    // taken, keeps the cached markup clean. ensureVideoElement() above is
    // the backstop for the rare case a stale element still slips through.
    this.handleTurboBeforeCache = this.handleTurboBeforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.handleTurboBeforeCache)
  }

  disconnect() {
    document.removeEventListener("feed:mute-change", this.muteListener)
    document.removeEventListener("turbo:before-cache", this.handleTurboBeforeCache)

    if (currentlyPlayingController === this) {
      currentlyPlayingController = null
    }

    this.destroyPlayer()
  }

  handleTurboBeforeCache() {
    this.destroyPlayer()
  }

  destroyPlayer() {
    this.element.videoController = null

    if (this.player && !this.player.isDisposed()) {
      this.player.dispose()
    }
  }

  // Makes sure there is a clean, unprocessed <video> element to hand to
  // Video.js, recovering from a stale/cached DOM if needed. Returns false
  // if there's genuinely nothing usable at all, in which case connect()
  // bails out rather than erroring.
  ensureVideoElement() {
    const key = this.cacheKey()

    if (this.hasVideoTarget && this.isPristine(this.videoTarget)) {
      pristineVideoCache.set(key, this.videoTarget.cloneNode(true))
      return true
    }

    const storedClone = pristineVideoCache.get(key)
    if (!storedClone) return false

    const freshVideo = storedClone.cloneNode(true)

    if (this.hasVideoTarget) {
      // Put it back exactly where the stale one was.
      this.videoTarget.replaceWith(freshVideo)
    } else {
      // The target vanished entirely (e.g. Video.js's dispose() removed
      // it) - append it back into the wrapper.
      this.element.appendChild(freshVideo)
    }

    return true
  }

  isPristine(element) {
    return !element.classList.contains("vjs-tech")
  }

  cacheKey() {
    // The id rendered in the ERB (e.g. "video-js-42") is stable across a
    // Turbo cache restore even if the DOM node itself isn't the same
    // object; falling back to the element reference covers the case
    // where no id is present.
    return this.element.id || this.element
  }

  volumeChanged = () => {
    const muted = this.player.muted()

    if (globalThis.feedMuted === muted) return

    globalThis.feedMuted = muted

    document.dispatchEvent(
      new CustomEvent("feed:mute-change", {
        detail: { muted }
      })
    )
  }

  muteListener = (event) => {
    if (!this.player || this.player.isDisposed()) return
    this.player.muted(event.detail.muted)
  }

  isPlaying() {
    return !!this.player && !this.player.isDisposed() && !this.player.paused()
  }

  // Runs `task` only after any previously queued play()/pause() call for
  // this player has finished, so they can never overlap or interrupt
  // each other.
  enqueue(task) {
    this.operationQueue = this.operationQueue.then(task).catch(() => { })
    return this.operationQueue
  }

  play() {
    return this.enqueue(async () => {
      if (!this.player || this.player.isDisposed()) return
      if (this.isPlaying()) return

      try {
        this.player.muted(globalThis.feedMuted)
        await this.player.play()
      } catch (err) {
        // Browsers block autoplay for unmuted video without a user
        // gesture. If globalThis.feedMuted was false (e.g. the user
        // unmuted a previous video) and this attempt got rejected for
        // that reason, fall back to a muted attempt rather than silently
        // never playing.
        if (!this.player.muted()) {
          try {
            this.player.muted(true)
            await this.player.play()
          } catch (err2) {
            // Still blocked - nothing more we can do without a gesture.
          }
        }
      }
    })
  }

  pause() {
    return this.enqueue(async () => {
      if (!this.player || this.player.isDisposed()) return
      if (this.player.paused()) return

      this.player.pause()
    })
  }

  addCenterPlayButton() {
    const wrapper = this.element.parentNode
    if (!wrapper) return

    // Guard against a duplicate button if this controller ever connects
    // more than once for the same wrapper (e.g. after a cache restore).
    wrapper.querySelector(":scope > .center-play-btn")?.remove()

    wrapper.classList.add("custom-video-wrapper", "position-relative")
    this.element.classList.add("custom-controls-enabled")

    const btn = document.createElement("div")
    btn.classList.add(
      "center-play-btn",
      "size-70",
      "position-absolute",
      "top-50",
      "start-50",
      "translate-middle",
      "d-flex",
      "align-items-center",
      "justify-content-center",
      "rounded-circle",
      "cursor-pointer"
    )

    btn.innerHTML = this.playIcon()

    btn.addEventListener("click", () => {
      if (this.player.paused()) {
        this.player.play()
      } else {
        this.player.pause()
      }
    })

    wrapper.appendChild(btn)

    this.player.on("play", () => (btn.innerHTML = this.pauseIcon()))
    this.player.on("pause", () => (btn.innerHTML = this.playIcon()))
  }

  playIcon() {
    return `
      <svg class="pe-none" width="40" height="40" viewBox="0 0 24 24" fill="white">
        <polygon points="8,5 19,12 8,19"></polygon>
      </svg>
    `
  }

  pauseIcon() {
    return `
      <svg class="pe-none" width="40" height="40" viewBox="0 0 24 24" fill="white">
        <rect x="6" y="5" width="4" height="14"></rect>
        <rect x="14" y="5" width="4" height="14"></rect>
      </svg>
    `
  }
}
